import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import UWEEntityApi from '../api/UWEEntityApi';

import customFormats from '../util/customFormats';
import FormComponent from '../components/FormComponent';
import { getObject, interpolateMap, mapObject, setObject } from '../util/mapObject';
import { useSideChannel } from '../util/useSideChannel';
import formValidation from '../util/formValidation';

import Effect from '../components/Effect';
import HandleUWEEntityUpdate from '../components/HandleUWEEntityUpdate';
import FormNavigationPanel from './FormNavigationPanel';

import { FORM_SUBMIT_BUTTONS, FORM_ONLY_SUBMIT_BUTTON } from '../constants';
import { AuthContext } from '../context/AuthContext';
import IntegrationsApi from '../api/IntegrationsApi';
import AppContext from '../context/AppContext';
import UWEFormContext from '../context/UWEFormContext';
import { useLingui } from '@lingui/react/macro';
import { resolveI18nTree } from '../util/i18nTree';

function UWEEntityFormComponent({
  slug,
  entityType,
  page,
  buildFormContext,
  secondaryClientForm,
  renderFormSubmitted: propRenderFormSubmitted,
  noSubmit,
  canAddClients,
  hasSaveProgressButton = true,
  commentFieldsMap,
  overrideOnSubmit,
  submitButtons: propSubmitButtons,
  displayName,
  onSubmit: propOnSubmit,
  wfAccessToken,
  ...props
}) {
  const submitButtons = propSubmitButtons || (noSubmit ? {} : hasSaveProgressButton ? FORM_SUBMIT_BUTTONS : FORM_ONLY_SUBMIT_BUTTON);

  const hasSecondaryClients = !!secondaryClientForm;

  return FormComponent({
    wfAccessToken,
    noHtml5Validate: true,
    parseProps: ({ readonly, currentEntity, onlySave, ...rest }) => {
      const { i18n } = useLingui();
      // submitButtons here is the raw, JSON-authored button config (closed over once, at this
      // factory's single call in UWEFormComponent's [formArgs]-keyed memo — not locale-aware
      // on its own) — this is the actual source FormComponent.jsx's own submitButtons ends up
      // using, since it always prefers this prop over its own localization pass. Resolving
      // i18n here, reactively per render, is what makes button text/confirm dialogs translate.
      const buttons = useMemo(
        () =>
          resolveI18nTree(
            onlySave ? (submitButtons.saveOnly ? { saveOnly: submitButtons.saveOnly } : {}) : submitButtons,
            i18n.locale
          ),
        [onlySave, i18n.locale]
      );

      const auth = useContext(AuthContext);
      const uweFormContext = useContext(UWEFormContext);
      const appContext = useContext(AppContext);

      const _readonly = readonly != null ? readonly : Object.keys(buttons).length === 0;
      return {
        readonly: _readonly,
        currentEntity,
        appContext,
        uweFormContext,
        auth,
        entityType,
        submitButtons: buttons,
      };
    },
    loadData: ({ currentEntity }) => Promise.resolve(currentEntity),
    buildFormContext: (...args) => {
      const { props } = args[0];
      const curUWEEntity = props.currentEntity || {};
      const { user: auth = {} } = props.auth || {};
      const { user = {} } = auth;

      return {
        auth,
        user,
        integration: {
          url: IntegrationsApi.GetOperationUrl,
          call: IntegrationsApi.CallOperationUrl,
        },
        draftId: curUWEEntity?.draftId,
        status: props.currentStatus,
        fileField: { postArgs: { entityId: (curUWEEntity || {}).id || '' } },
        clientEmail: getObject(curUWEEntity, 'clients[0].retailClientBasicInfo.email'),
        clientFirstName: getObject(curUWEEntity, 'clients[0].retailClientBasicInfo.firstName'),
        clientLastName: getObject(curUWEEntity, 'clients[0].retailClientBasicInfo.lastName'),
        ...(buildFormContext ? buildFormContext(...args) : {}),
      };
    },
    beforeRenderHook({
      currentFormData,
      saveCurrentFormObject,
      setFormDefinition,
      getCurrentFormObject,
      setExtraErrors,
      setFormObject,
      submitButtons,
      navRef,
      onSubmitRef,
    }) {
      const [clientIdx, _setClientIdx] = useState(null);
      const [clientErrors, setClientErrors] = useState([]);
      const setClientIdx = useCallback(
        idx => {
          saveCurrentFormObject().then(() => {
            _setClientIdx(idx | 0);
          });
        },
        [_setClientIdx, saveCurrentFormObject]
      );

      useEffect(() => {
        if (clientIdx === null) return;

        if (!hasSecondaryClients || clientIdx === 0) {
          setFormDefinition();
          setExtraErrors();
        } else {
          const { schema, uiSchema, objectMap, commentFieldsMap } = secondaryClientForm;
          setFormDefinition(schema, uiSchema, interpolateMap(objectMap, { idx: clientIdx }), commentFieldsMap, clientIdx);
          setExtraErrors();
        }
      }, [clientIdx]);

      const addNewClient = useCallback(() => {
        const uweEntityObj = getCurrentFormObject();
        const clients = (uweEntityObj.clients || []).length === 0 ? [{}] : uweEntityObj.clients || [];
        const idx = clients.length;

        return saveCurrentFormObject({
          ...uweEntityObj,
          clients: [...clients, {}],
        }).then(() => _setClientIdx(idx));
      }, [getCurrentFormObject, saveCurrentFormObject]);

      const validateCurrentClient = useCallback(
        object => {
          const errorSchema = validateClient(object === undefined ? getCurrentFormObject() : object, clientIdx | 0, props, secondaryClientForm);
          const hasErrors = !!Object.keys(errorSchema).length;

          const newClientErrors = [...clientErrors];
          newClientErrors[clientIdx | 0] = hasErrors;

          if (hasErrors) {
            setExtraErrors(errorSchema);
          } else {
            setExtraErrors();
          }

          setClientErrors(newClientErrors);

          return !hasErrors;
        },
        [clientIdx, getCurrentFormObject]
      );

      const validateAllClients = useCallback(
        object => {
          object = object === undefined ? getCurrentFormObject() : object;

          // Short circuit validation on Send Back
          if (object.IsSentBack) {
            return true;
          }

          const validations = object.clients.map((cl, idx) => validateClient(object, idx, props, secondaryClientForm));
          const curIdx = clientIdx | 0;

          const clientErrors = validations.map(v => !!Object.keys(v).length);
          const hasErrors = clientErrors.some(v => v);

          if (clientErrors[curIdx]) {
            setExtraErrors(validations[curIdx]);
          } else {
            setExtraErrors();
          }

          setClientErrors(clientErrors);

          return !hasErrors;
        },
        [clientIdx, getCurrentFormObject]
      );

      const cfSideChannel = useSideChannel();
      useEffect(() => {
        cfSideChannel.publish({
          clientIdx,
          currentFormData,
          addNewClient,
          setClientIdx,
          saveCurrentFormObject,
          validateCurrentClient,
          validateAllClients,
          canAddClients,
          navRef,
          onSubmitRef,
        });
      }, [
        clientIdx,
        currentFormData,
        setClientIdx,
        addNewClient,
        saveCurrentFormObject,
        validateCurrentClient,
        validateAllClients,
        canAddClients,
        navRef,
        onSubmitRef,
      ]);

      if (submitButtons.submit && submitButtons.submit.btnProps) {
        submitButtons.submit.btnProps.cfSideChannel = cfSideChannel;
      }

      return {
        addNewClient,
        clientIdx: clientIdx || 0,
        clientErrors,
        setClientIdx,
      };
    },
    renderNavigation({ title, navRef, formRef, hasUnsavedChanges }) {
      return !props.withoutNavigation ? (
        <FormNavigationPanel formTitle={title} ref={navRef} formElement={formRef && formRef.current} hasUnsavedChanges={hasUnsavedChanges} />
      ) : null;
    },
    submitButtons,
    async onSubmit({ object, commentFieldsMap: curFieldMap }, scope) {
      const { currentEntity, entityType, slug, auth, appContext, uweFormContext } = scope.props;
      const commentFM = curFieldMap || commentFieldsMap;

      if (commentFM) {
        Object.entries(commentFM).forEach(([destPath, srcPath]) => {
          const currentVal = mapObject(
            object,
            {
              value: srcPath,
            },
            {},
            {}
          ).value;

          setObject(object, destPath, currentVal);
        });
      }

      if (uweFormContext?.pendingNotes) {
        object.comments = object.comments ?? [];
        uweFormContext.pendingNotes.forEach(({ message }) => object.comments.push(message));
        uweFormContext.setPendingNotes();
      }

      if (currentEntity?.id) {
        const payload = {
          id: currentEntity.id,
          page,
          formData: {
            action: object.action,
            isSentBack: object.isSentBack,
            isPreview: object.isPreview,
            sendBackReason: object.sendBackReason,
            sendBackTo: object.sendBackTo,
            flags: object.flags,
            closeReason: object.closeReason,
            comments: object.comments,
            data: object.data,
          },
          wfAccessToken,
        };

        return overrideOnSubmit
          ? overrideOnSubmit({
              action: 'updateUWEEntity',
              payload,
            })
          : appContext.isAnonymous
          ? UWEEntityApi.updateUWEEntityAsAnonymous({ payload, slug, tenant: appContext.tenant })
          : UWEEntityApi.updateUWEEntity(payload);
      } else {
        const payload = {
          page,
          formData: {
            entityTypeId: entityType.id,
            ...object,
          },
          wfAccessToken,
        };

        return overrideOnSubmit
          ? overrideOnSubmit({
              action: 'createUWEEntity',
              payload,
            })
          : appContext.isAnonymous
          ? UWEEntityApi.createUWEEntityAsAnonymous({ payload, slug, tenant: appContext.tenant })
          : UWEEntityApi.createUWEEntity(payload);
      }
    },
    // For some reason, this renders if propRenderFormSubmitted is FALSY, instead of TRUTHY
    renderFormSubmitted:
      propRenderFormSubmitted ||
      (propRenderFormSubmitted === false
        ? null
        : props => <HandleUWEEntityUpdate {...props} slug={slug} entityType={entityType} onSubmit={propOnSubmit} />),
    customFormats,
    displayName: `UWEEntityForm${displayName}`,
    ...props,
  });
}

function validateClient(formObject, idx, props, secondaryClientForm) {
  idx = idx | 0;
  let schema = null;
  let objectMap = null;

  if (!secondaryClientForm || idx === 0) {
    schema = props.schema;
    objectMap = props.objectMap;
  } else {
    schema = secondaryClientForm.schema;
    objectMap = interpolateMap(secondaryClientForm.objectMap, { idx });
  }

  return formValidation(formObject, schema, objectMap, customFormats);
}

export default UWEEntityFormComponent;
